/**
 * Active-branch store.
 *
 * Tiny pub/sub singleton (no Redux dependency) that holds the branch the
 * user has selected for this session. Persisted to localStorage so the
 * choice survives reloads.
 *
 * Components subscribe to `subscribe(...)` to render the active branch
 * name; HTTP layer reads `getActiveBranchId()` from the axios request
 * interceptor in `setupBranchHeader.ts`.
 */

import { useEffect, useState } from 'react';

export interface Branch {
  id: string;
  code?: string;
  name: string;
}

const STORAGE_KEY = 'kss.activeBranch';

let active: Branch | null = readPersisted();
const listeners = new Set<(b: Branch | null) => void>();

function readPersisted(): Branch | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Branch;
    if (!parsed?.id) return null;
    return parsed;
  } catch {
    return null;
  }
}

function persist(b: Branch | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (b) window.localStorage.setItem(STORAGE_KEY, JSON.stringify(b));
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore quota / private-mode errors */
  }
}

export function getActiveBranch(): Branch | null {
  return active;
}

export function getActiveBranchId(): string | null {
  return active?.id ?? null;
}

export function setActiveBranch(b: Branch | null): void {
  active = b;
  persist(b);
  for (const l of listeners) l(b);
}

export function subscribe(fn: (b: Branch | null) => void): () => void {
  listeners.add(fn);
  fn(active);
  return () => listeners.delete(fn);
}

/** React hook — re-renders the host component on branch change. */
export function useActiveBranch(): Branch | null {
  const [b, setB] = useState<Branch | null>(active);
  useEffect(() => subscribe(setB), []);
  return b;
}
